import type { FigureField, Figures, KpiKey } from '../../lib/kpi';
import { formatCents, formatCount, formatShare } from './format';

export type KpiKind = 'money' | 'count' | 'split';

export interface KpiDefinition {
  label: string;
  kind: KpiKind;
  /** The figure a card, a column and the chart read. A split reads this one
   *  and buyback_share beside it. */
  field: FigureField;
  hint: string;
}

/** Every KPI the backend can grant, with what it means. The backend decides
 *  which ones a viewer gets; this only says how to show them. */
export const KPI_CATALOG: Record<KpiKey, KpiDefinition> = {
  sales_volume: {
    label: 'Sales volume', kind: 'money', field: 'sales_volume_cents',
    hint: 'Pack price of the rounds closed in the period.',
  },
  sales_count: {
    label: 'Sales count', kind: 'count', field: 'sales_count',
    hint: 'Rounds closed: one card, sold back or shipped.',
  },
  buyback_volume: {
    label: 'Buyback volume', kind: 'money', field: 'buyback_volume_cents',
    hint: 'What the rounds paid out in money.',
  },
  buyback_count: {
    label: 'Buyback count', kind: 'count', field: 'buyback_count',
    hint: 'Rounds paid out in money.',
  },
  redemption_vs_buyback: {
    label: 'Redemption % vs Buyback %', kind: 'split', field: 'redemption_share',
    hint: 'Rounds closed by shipping the card, against rounds sold back.',
  },
  redemption_fmv: {
    label: 'Redemption FMV', kind: 'money', field: 'redemption_fmv_cents',
    hint: 'Market value of the cards shipped to players.',
  },
  cards_redeemed: {
    label: 'Cards redeemed', kind: 'count', field: 'cards_redeemed',
    hint: 'Cards shipped to players.',
  },
  shipping_handling: {
    label: 'Shipping & handling charged', kind: 'money', field: 'shipping_handling_cents',
    hint: 'Delivery fees charged to players.',
  },
  refunds: {
    label: 'Refunds', kind: 'money', field: 'refunds_cents',
    hint: 'Value of the cards whose delivery fell through, money back for damaged cards, plus shipping and handling refunded.',
  },
  refunds_count: {
    label: 'Refunds count', kind: 'count', field: 'refunds_count',
    hint: 'Cards whose delivery fell through.',
  },
  ggr: {
    label: 'GGR', kind: 'money', field: 'ggr_cents',
    hint: 'Sales less buybacks and the value of the cards shipped.',
  },
  studio_commission: {
    label: 'Studio commission', kind: 'money', field: 'studio_commission_cents',
    hint: 'Each operator’s contracted share of its GGR, month by month.',
  },
};

/** A KPI's value as the viewer reads it. */
export function formatKpi(key: KpiKey, figures: Figures, currency: string): string {
  const kpi = KPI_CATALOG[key];
  if (kpi.kind === 'split') {
    return `${formatShare(figures.redemption_share)} / ${formatShare(figures.buyback_share)}`;
  }

  const value = figures[kpi.field];
  return kpi.kind === 'money' ? formatCents(value, currency) : formatCount(value);
}

/** The KPIs a bar per day makes sense for: sums, not ratios. */
export function chartableKpis(keys: KpiKey[]): KpiKey[] {
  return keys.filter((key) => KPI_CATALOG[key].kind !== 'split');
}
